import React, { useState, useEffect, useMemo } from "react";
import {
  Container,
  Group,
  Button,
  Text,
  Badge,
  ActionIcon,
  Menu,
  LoadingOverlay,
  Box,
  SimpleGrid,
  ScrollArea,
  TextInput,
  Modal,
  Stack,
  Switch,
  Card,
  Radio,
  Divider,
} from "@mantine/core";
import {
  IconPlus,
  IconEdit,
  IconDotsVertical,
  IconTrash,
  IconCopy,
  IconSearch,
  IconRefresh,
  IconPlayerPlay,
  IconFolderOff,
} from "@tabler/icons-react";
import { Zap, MessageSquare, Calendar, GitBranch } from "lucide-react";
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  deleteDoc,
  updateDoc,
  addDoc,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../../firebase/FirebaseCredential";
import { useAppSelector } from "../../hooks/hooks";
import { RootState } from "../../store/store";
import { useNavigate } from "react-router-dom";
import { BenchTable } from "../pages/UI";
import BenchStatCard from "../UIComponent/Components/BenchStatCard";
import BenchBreadcrumbs from "../UIComponent/Components/BenchBreadcrumbs";
import { toast } from "react-toastify";
import BenchSettingsBreadcrumbs from "../UIComponent/Components/BenchSettingsBreadcrumbs";

export interface WhatsAppFlowRecord {
  id: string;
  name: string;
  is_active: boolean;
  company_id: string;
  nodes?: any[];
  edges?: any[];
  created_at?: any;
  updated_at?: any;
}

const TEMPLATES = [
  {
    id: "blank",
    title: "Blank WhatsApp Flow",
    description: "Start with an empty canvas and a customizable trigger block.",
    icon: Zap,
    nodes: [
      {
        id: "trigger_1",
        type: "triggerNode",
        position: { x: 100, y: 200 },
        data: {
          label: "Flow Trigger",
          triggerType: "KEYWORD",
          keywords: ["HI", "HELLO"],
        },
      },
    ],
    edges: [],
  },
  {
    id: "lead_gen",
    title: "Lead Qualification Flow",
    description:
      "Greet leads on WhatsApp, capture requirement, and route accordingly.",
    icon: MessageSquare,
    nodes: [
      {
        id: "trigger_1",
        type: "triggerNode",
        position: { x: 80, y: 180 },
        data: {
          label: "Flow Trigger",
          triggerType: "KEYWORD",
          keywords: ["JOIN", "INFO", "PRICE"],
        },
      },
      {
        id: "msg_1",
        type: "messageNode",
        position: { x: 440, y: 180 },
        data: {
          label: "Welcome Message",
          body: "Hello! Welcome to our fitness club. How can we help you today?",
          headerType: "NONE",
          buttons: [
            { id: "b1", text: "Membership Plans" },
            { id: "b2", text: "Free Trial Pass" },
          ],
        },
      },
    ],
    edges: [
      {
        id: "e1",
        source: "trigger_1",
        target: "msg_1",
        animated: true,
        type: "default",
      },
    ],
  },
  {
    id: "booking",
    title: "Appointment / Trial Booking Flow",
    description:
      "Allow contacts to select their preferred visit date & time slot.",
    icon: Calendar,
    nodes: [
      {
        id: "trigger_1",
        type: "triggerNode",
        position: { x: 80, y: 180 },
        data: {
          label: "Flow Trigger",
          triggerType: "KEYWORD",
          keywords: ["BOOK", "TRIAL", "VISIT"],
        },
      },
      {
        id: "slot_1",
        type: "dateTimeNode",
        position: { x: 440, y: 180 },
        data: {
          label: "Select Slot",
          prompt: "Please select your preferred visit date & slot:",
          dateOptions: ["Today", "Tomorrow", "Weekend"],
          slots: [
            "10:00 AM - 12:00 PM",
            "04:00 PM - 06:00 PM",
            "06:00 PM - 08:00 PM",
          ],
          variable: "preferred_slot",
        },
      },
    ],
    edges: [
      {
        id: "e1",
        source: "trigger_1",
        target: "slot_1",
        animated: true,
        type: "default",
      },
    ],
  },
];

export const FlowListPage: React.FC = () => {
  const navigate = useNavigate();
  const companyId = useAppSelector(
    (state: RootState) => state.session?.company_id,
  );

  const [flows, setFlows] = useState<WhatsAppFlowRecord[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");

  // Create Modal State
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [newFlowName, setNewFlowName] = useState("");
  const [selectedTemplate, setSelectedTemplate] = useState("blank");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Delete Confirm Modal State
  const [flowToDelete, setFlowToDelete] = useState<WhatsAppFlowRecord | null>(
    null,
  );

  useEffect(() => {
    fetchFlows();
  }, [companyId]);

  const fetchFlows = async () => {
    if (!companyId) return;
    setIsLoading(true);
    try {
      const q = query(
        collection(db, "whatsapp_automation_flow"),
        where("company_id", "==", companyId),
      );
      const snapshot = await getDocs(q);
      const data: WhatsAppFlowRecord[] = [];
      snapshot.forEach((docSnap) => {
        data.push({ id: docSnap.id, ...docSnap.data() } as WhatsAppFlowRecord);
      });

      // Sort client-side by updated_at or created_at descending
      data.sort((a, b) => {
        const timeA =
          a.updated_at?.toMillis?.() || a.created_at?.toMillis?.() || 0;
        const timeB =
          b.updated_at?.toMillis?.() || b.created_at?.toMillis?.() || 0;
        return timeB - timeA;
      });

      setFlows(data);
    } catch (error) {
      console.error("Error fetching WhatsApp flows:", error);
      toast.error("Failed to load WhatsApp flows");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateFlow = async () => {
    if (!newFlowName.trim()) {
      toast.warn("Please enter a flow name");
      return;
    }
    if (!companyId) return;

    setIsSubmitting(true);
    try {
      const template =
        TEMPLATES.find((t) => t.id === selectedTemplate) || TEMPLATES[0];

      const newDocRef = await addDoc(
        collection(db, "whatsapp_automation_flow"),
        {
          name: newFlowName.trim(),
          is_active: false,
          company_id: companyId,
          nodes: template.nodes,
          edges: template.edges,
          created_at: serverTimestamp(),
          updated_at: serverTimestamp(),
        },
      );

      toast.success("Flow created successfully!");
      setIsCreateModalOpen(false);
      setNewFlowName("");
      navigate(`/flowbuilder/${newDocRef.id}`);
    } catch (error) {
      console.error("Error creating flow:", error);
      toast.error("Failed to create flow");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleToggleStatus = async (flow: WhatsAppFlowRecord) => {
    try {
      const nextStatus = !flow.is_active;
      await updateDoc(doc(db, "whatsapp_automation_flow", flow.id), {
        is_active: nextStatus,
        updated_at: serverTimestamp(),
      });
      setFlows((prev) =>
        prev.map((f) =>
          f.id === flow.id ? { ...f, is_active: nextStatus } : f,
        ),
      );
      toast.success(
        nextStatus
          ? `"${flow.name}" is now Active`
          : `"${flow.name}" set to Draft`,
      );
    } catch (error) {
      console.error("Error updating flow status:", error);
      toast.error("Failed to update status");
    }
  };

  const handleDuplicateFlow = async (flow: WhatsAppFlowRecord) => {
    if (!companyId) return;
    try {
      setIsLoading(true);
      const duplicateData = {
        name: `${flow.name} (Copy)`,
        is_active: false,
        company_id: companyId,
        nodes: flow.nodes || [],
        edges: flow.edges || [],
        created_at: serverTimestamp(),
        updated_at: serverTimestamp(),
      };
      await addDoc(collection(db, "whatsapp_automation_flow"), duplicateData);
      toast.success("Flow duplicated successfully");
      fetchFlows();
    } catch (error) {
      console.error("Error duplicating flow:", error);
      toast.error("Failed to duplicate flow");
      setIsLoading(false);
    }
  };

  const handleDeleteFlow = async () => {
    if (!flowToDelete) return;
    try {
      await deleteDoc(doc(db, "whatsapp_automation_flow", flowToDelete.id));
      setFlows((prev) => prev.filter((f) => f.id !== flowToDelete.id));
      toast.success("Flow deleted successfully");
      setFlowToDelete(null);
    } catch (error) {
      console.error("Error deleting flow:", error);
      toast.error("Failed to delete flow");
    }
  };

  const filteredFlows = useMemo(() => {
    if (!searchQuery.trim()) return flows;
    const lower = searchQuery.toLowerCase();
    return flows.filter((f) => f.name.toLowerCase().includes(lower));
  }, [flows, searchQuery]);

  const activeCount = useMemo(
    () => flows.filter((f) => f.is_active).length,
    [flows],
  );
  const draftCount = useMemo(
    () => flows.filter((f) => !f.is_active).length,
    [flows],
  );

  const statsData = [
    {
      title: "Total Flows",
      count: flows.length,
      description: "WhatsApp automation flows",
      icon: <GitBranch size={22} color="#0ca678" />,
      bgColor: "#e6fcf5",
      textColor: "#0ca678",
      descColor: "#20c997",
    },
    {
      title: "Active Flows",
      count: activeCount,
      description: "Live on WhatsApp",
      icon: <IconPlayerPlay size={22} color="#2b8a3e" />,
      bgColor: "#ebfbee",
      textColor: "#2b8a3e",
      descColor: "#40c057",
    },
    {
      title: "Draft Flows",
      count: draftCount,
      description: "In development / inactive",
      icon: <Zap size={22} color="#f59f00" />,
      bgColor: "#fff9db",
      textColor: "#f59f00",
      descColor: "#fab005",
    },
  ];

  const formatDate = (timestamp: any) => {
    if (!timestamp) return "—";
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return date.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    } catch {
      return "—";
    }
  };

  const getTriggerType = (flow: WhatsAppFlowRecord) => {
    const triggerNode = flow.nodes?.find((n) => n.type === "triggerNode");
    if (!triggerNode) return "Default Trigger";
    const type = triggerNode.data?.triggerType;
    if (type === "KEYWORD") {
      const kw = triggerNode.data?.keywords || [];
      return kw.length > 0
        ? `Keyword (${kw.slice(0, 2).join(", ")})`
        : "Keyword Trigger";
    }
    if (type === "ALL_MESSAGES") return "All Incoming Messages";
    if (type === "WEBHOOK") return "External Webhook";
    return "Start Trigger";
  };

  return (
    <div
      style={{ position: "relative", minHeight: "80vh", paddingBottom: "2rem" }}
    >
      <LoadingOverlay visible={isLoading} overlayBlur={2} />

      <Container size="lg" pt="md">
        {/* Breadcrumbs */}
        <BenchSettingsBreadcrumbs links={[{ label: "WhatsApp Flows" }]} />
        <Divider my="lg" />
        {/* Stats Cards */}
        <BenchStatCard data={statsData} />

        {/* Header Actions */}
        <Group position="apart" align="center" mt="xl" mb="md">
          <Box>
            <Text fw={700} fz="xl" color="dark.8">
              WhatsApp Automation Flows
            </Text>
            <Text fz="sm" color="dimmed">
              Design and manage visual conversation flows for WhatsApp
              automation
            </Text>
          </Box>

          <Group spacing="sm">
            <TextInput
              placeholder="Search flows..."
              icon={<IconSearch size={16} />}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              size="sm"
              style={{ width: 240 }}
            />
            <ActionIcon
              variant="default"
              size="lg"
              radius="md"
              onClick={fetchFlows}
              title="Refresh flows"
            >
              <IconRefresh size={18} />
            </ActionIcon>
            <Button
              leftIcon={<IconPlus size={18} />}
              color="teal"
              radius="md"
              onClick={() => {
                setNewFlowName("");
                setSelectedTemplate("blank");
                setIsCreateModalOpen(true);
              }}
            >
              Create Flow
            </Button>
          </Group>
        </Group>

        {/* Table View */}
        {!isLoading && filteredFlows.length > 0 && (
          <ScrollArea mt="md">
            <BenchTable>
              <thead>
                <tr>
                  <th style={{ width: "35%" }}>Flow Name</th>
                  <th style={{ width: "20%" }}>Trigger</th>
                  <th style={{ width: "15%" }}>Steps</th>
                  <th style={{ width: "15%" }}>Status</th>
                  <th style={{ width: "15%" }}>Last Modified</th>
                  <th style={{ width: "10%", textAlign: "center" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredFlows.map((flow) => {
                  const nodeCount = flow.nodes?.length || 1;
                  return (
                    <tr key={flow.id}>
                      {/* Name */}
                      <td>
                        <Group spacing="sm" noWrap>
                          <Box
                            sx={{
                              width: 36,
                              height: 36,
                              borderRadius: 8,
                              backgroundColor: flow.is_active
                                ? "#e6fcf5"
                                : "#f1f3f5",
                              color: flow.is_active ? "#0ca678" : "#868e96",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              flexShrink: 0,
                            }}
                          >
                            <GitBranch size={20} />
                          </Box>
                          <Box sx={{ minWidth: 0 }}>
                            <Text
                              fw={600}
                              fz="sm"
                              sx={{
                                cursor: "pointer",
                                "&:hover": { color: "#0ca678" },
                              }}
                              onClick={() =>
                                navigate(`/flowbuilder/${flow.id}`)
                              }
                            >
                              {flow.name}
                            </Text>
                            <Text fz="xs" color="dimmed">
                              ID: {flow.id.slice(0, 8)}...
                            </Text>
                          </Box>
                        </Group>
                      </td>

                      {/* Trigger */}
                      <td>
                        <Badge
                          variant="light"
                          color="teal"
                          size="sm"
                          radius="sm"
                          leftSection={<Zap size={10} />}
                        >
                          {getTriggerType(flow)}
                        </Badge>
                      </td>

                      {/* Node Count */}
                      <td>
                        <Text fz="sm" fw={500} color="dark.6">
                          {nodeCount} {nodeCount === 1 ? "block" : "blocks"}
                        </Text>
                      </td>

                      {/* Status */}
                      <td>
                        <Group spacing="xs">
                          <Switch
                            checked={flow.is_active}
                            color="teal"
                            size="sm"
                            onChange={() => handleToggleStatus(flow)}
                          />
                          <Badge
                            color={flow.is_active ? "teal" : "gray"}
                            variant="filled"
                            size="xs"
                          >
                            {flow.is_active ? "Active" : "Draft"}
                          </Badge>
                        </Group>
                      </td>

                      {/* Date */}
                      <td>
                        <Text fz="sm" color="dimmed">
                          {formatDate(flow.updated_at || flow.created_at)}
                        </Text>
                      </td>

                      {/* Actions */}
                      <td>
                        <Group position="center" spacing={4} noWrap>
                          <ActionIcon
                            variant="subtle"
                            color="teal"
                            title="Edit Flow"
                            onClick={() => navigate(`/flowbuilder/${flow.id}`)}
                          >
                            <IconEdit size={16} />
                          </ActionIcon>
                          <Menu position="bottom-end" shadow="md" withinPortal>
                            <Menu.Target>
                              <ActionIcon variant="subtle" color="gray">
                                <IconDotsVertical size={16} />
                              </ActionIcon>
                            </Menu.Target>
                            <Menu.Dropdown>
                              <Menu.Item
                                icon={<IconEdit size={14} />}
                                onClick={() =>
                                  navigate(`/flowbuilder/${flow.id}`)
                                }
                              >
                                Open in Builder
                              </Menu.Item>
                              <Menu.Item
                                icon={<IconCopy size={14} />}
                                onClick={() => handleDuplicateFlow(flow)}
                              >
                                Duplicate Flow
                              </Menu.Item>
                              <Menu.Divider />
                              <Menu.Item
                                color="red"
                                icon={<IconTrash size={14} />}
                                onClick={() => setFlowToDelete(flow)}
                              >
                                Delete Flow
                              </Menu.Item>
                            </Menu.Dropdown>
                          </Menu>
                        </Group>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </BenchTable>
          </ScrollArea>
        )}

        {/* Empty State */}
        {!isLoading && filteredFlows.length === 0 && (
          <Card
            withBorder
            p="xl"
            radius="md"
            mt="lg"
            sx={{
              textAlign: "center",
              backgroundColor: "#fcfcfc",
              borderStyle: "dashed",
            }}
          >
            <Stack align="center" spacing="sm" py="xl">
              <Box
                sx={{
                  width: 64,
                  height: 64,
                  borderRadius: "50%",
                  backgroundColor: "#e6fcf5",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#0ca678",
                }}
              >
                {searchQuery ? (
                  <IconFolderOff size={32} />
                ) : (
                  <GitBranch size={32} />
                )}
              </Box>

              <Text fw={700} fz="lg" color="dark.8">
                {searchQuery
                  ? "No matching flows found"
                  : "No WhatsApp flows created yet"}
              </Text>

              <Text fz="sm" color="dimmed" maw={440}>
                {searchQuery
                  ? "Try searching with a different term or clear the filter."
                  : "Automate responses, capture leads, and schedule appointments with interactive WhatsApp flows."}
              </Text>

              {searchQuery ? (
                <Button
                  variant="subtle"
                  color="teal"
                  onClick={() => setSearchQuery("")}
                >
                  Clear Search
                </Button>
              ) : (
                <Button
                  leftIcon={<IconPlus size={16} />}
                  color="teal"
                  radius="md"
                  mt="sm"
                  onClick={() => {
                    setNewFlowName("");
                    setSelectedTemplate("blank");
                    setIsCreateModalOpen(true);
                  }}
                >
                  Create Your First Flow
                </Button>
              )}
            </Stack>
          </Card>
        )}
      </Container>

      {/* Create Flow Modal */}
      <Modal
        opened={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        title={
          <Text fw={700} fz="md">
            Create WhatsApp Automation Flow
          </Text>
        }
        size="md"
        radius="md"
      >
        <Stack spacing="md">
          <TextInput
            label="Flow Name"
            placeholder="e.g. Lead Qualification Flow"
            value={newFlowName}
            onChange={(e) => setNewFlowName(e.target.value)}
            required
            autoFocus
          />

          <Box>
            <Text fw={500} fz="sm" mb="xs">
              Choose a Starter Template
            </Text>
            <Radio.Group
              value={selectedTemplate}
              onChange={setSelectedTemplate}
            >
              <Stack spacing="xs">
                {TEMPLATES.map((tmpl) => {
                  const Icon = tmpl.icon;
                  const isSelected = selectedTemplate === tmpl.id;
                  return (
                    <Card
                      key={tmpl.id}
                      withBorder
                      p="xs"
                      radius="md"
                      sx={{
                        cursor: "pointer",
                        borderColor: isSelected ? "#0ca678" : undefined,
                        backgroundColor: isSelected ? "#f8fdfb" : undefined,
                      }}
                      onClick={() => setSelectedTemplate(tmpl.id)}
                    >
                      <Group noWrap align="flex-start" spacing="sm">
                        <Radio value={tmpl.id} mt={2} color="teal" />
                        <Box sx={{ flex: 1 }}>
                          <Group spacing={6}>
                            <Icon size={14} color="#0ca678" />
                            <Text fw={600} fz="sm">
                              {tmpl.title}
                            </Text>
                          </Group>
                          <Text fz="xs" color="dimmed" mt={2}>
                            {tmpl.description}
                          </Text>
                        </Box>
                      </Group>
                    </Card>
                  );
                })}
              </Stack>
            </Radio.Group>
          </Box>

          <Group position="right" mt="md">
            <Button
              variant="default"
              onClick={() => setIsCreateModalOpen(false)}
            >
              Cancel
            </Button>
            <Button
              color="teal"
              onClick={handleCreateFlow}
              loading={isSubmitting}
            >
              Create & Open Builder
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        opened={!!flowToDelete}
        onClose={() => setFlowToDelete(null)}
        title={
          <Text fw={700} fz="md" color="red.7">
            Delete Flow
          </Text>
        }
        size="sm"
        radius="md"
      >
        <Text fz="sm" color="dark.7">
          Are you sure you want to delete{" "}
          <strong>"{flowToDelete?.name}"</strong>? This action cannot be undone.
        </Text>
        <Group position="right" mt="xl">
          <Button variant="default" onClick={() => setFlowToDelete(null)}>
            Cancel
          </Button>
          <Button color="red" onClick={handleDeleteFlow}>
            Delete Flow
          </Button>
        </Group>
      </Modal>
    </div>
  );
};

export default FlowListPage;
